import { Component, EventEmitter, forwardRef, HostBinding, Input, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { isEditableByMode } from '../utils/ontology-editing.util';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

type SelectValue = any;

@Component({
    selector: 'jhi-select',
    template: `
		@if (isEditable()) {
			<div class="jhi-ng-select-shell">
				<ng-select
					class="jhi-ng-select"
					[class.is-invalid]="showInvalidState"
					[class.is-valid]="showValidState"
					[items]="options"
					[disabled]="disabled"
					[placeholder]="placeholder"
					[clearable]="clearableComputed"
					[searchable]="searchable"
					[loading]="loading"
					[trackByFn]="trackByFn"
					[compareWith]="compareWithFn"
					[bindLabel]="labelField"
					[searchFn]="searchFn"
					[ngModelOptions]="{ standalone: true }"
					[ngModel]="selectedObject"
					(change)="onChangeInternal($event)"
					(blur)="markTouched()">

					<!-- Label en el input -->
					<ng-template ng-label-tmp let-item="item">
						{{ item ? labelFn(item) : '' }}
					</ng-template>

					<!-- Label en el dropdown -->
					<ng-template ng-option-tmp let-item="item">
						{{ labelFn(item) }}
					</ng-template>
				</ng-select>
			</div>
		} @else {
			<div class="form-control-plaintext">
				@if (link && modeView == ModeView.VIEW) {
					<a href="javascript:void(0)" (click)="openlink(value)">
						<truncate-span [textInput]="formatViewValue(value)"></truncate-span>
					</a>
				} @else {
					<truncate-span [textInput]="formatViewValue(value)"></truncate-span>
				}
			</div>
		}


    `,
    styles: [
        `
            :host {
                display: block;
            }

            .jhi-ng-select.is-invalid ::ng-deep .ng-select-container {
                border-left: 6px solid var(--bs-danger) !important;
                padding-left: calc(.75rem - 6px) !important;
            }

            .jhi-ng-select.is-valid ::ng-deep .ng-select-container {
                border-left: 6px solid var(--bs-success) !important;
                padding-left: calc(.75rem - 6px) !important;
            }
        `
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => SelectComponent),
            multi: true
        }
    ]
})
export class SelectComponent implements ControlValueAccessor, Validator {
    protected readonly ModeView = ModeView;
    @Input() modeView: ModeView = ModeView.VIEW;
    @Input() model: any;
    @Input() property?: string;

    @Input() disabled = false;
    @Input() link: string | null = null;

    @Input()
    set required(v: boolean) {
        this._required = !!v;
        this._validatorChange?.();
    }

    get required(): boolean {
        return this._required;
    }

    private _required = false;

    @Input() options: any[] = [];
    @Input() placeholder?: string;

    @Input() returnType: 'object' | 'id' = 'object';
    @Input() idField = 'id';
    @Input() labelField = 'name';

    @Input() labelFn: (opt: any) => string = (opt: any) => String(opt?.[this.labelField] ?? '');
    @Input() compareWith?: (a: any, b: any) => boolean;

    @Input() searchable = true;
    @Input() clearable = true;
    @Input() loading = false;

    @Output() valueChange = new EventEmitter<SelectValue>();

    value: SelectValue = null;

    selectedObject: any = null;

    constructor(private activeModal: NgbActiveModal,
                private router: Router) {
    }

    private onChange: (v: SelectValue) => void = () => {
    };
    private onTouched: () => void = () => {
    };
    private _validatorChange?: () => void;

    @HostBinding('class.modeView-create')
    get isCreateMode(): boolean {
        return this.modeView === ModeView.CREATE;
    }

    get clearableComputed(): boolean {
        return this.required ? false : this.clearable;
    }

    get showInvalidState(): boolean {
        return this.required && !this.hasValue();
    }

    get showValidState(): boolean {
        return this.hasValue();
    }

    private hasValue(): boolean {
        if (this.returnType === 'object') {
            return !!this.value && this.value?.[this.idField] !== null && this.value?.[this.idField] !== undefined;
        }

        return this.value !== null && this.value !== undefined && String(this.value).trim() !== '';
    }

    writeValue(v: SelectValue): void {
        this.value = v ?? null;

        if (this.returnType === 'object') {
            this.selectedObject = this.value;
        } else {
            if (this.value == null || this.value === '') {
                this.selectedObject = null;
            } else {
                const found = (this.options ?? []).find(o => String(o?.[this.idField]) === String(this.value));
                this.selectedObject = found ?? null;
            }
        }

        this._validatorChange?.();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    markTouched(): void {
        this.onTouched();
    }

    onChangeInternal(selected: any): void {
        this.selectedObject = selected ?? null;

        if (this.returnType === 'id') {
            this.value = this.selectedObject ? this.selectedObject?.[this.idField] : null;
        } else {
            this.value = this.selectedObject;
        }

        this.onChange(this.value);
        this.valueChange.emit(this.value);
        this._validatorChange?.();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!this.required) {
            return null;
        }

        const v = control.value;

        if (v === null || v === undefined || v === '') {
            return { required: true };
        }

        if (this.returnType === 'object') {
            const id = v?.[this.idField];
            if (id === null || id === undefined || String(id).trim() === '') {
                return { required: true };
            }
        }

        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this._validatorChange = fn;
    }

    searchFn = (term: string, item: any): boolean => {
        const normalizedTerm = (term ?? '').toLowerCase().trim();
        const value = (this.labelFn(item) ?? '').toLowerCase();

        return value.includes(normalizedTerm);
    };

    compareWithFn = (a: any, b: any): boolean => {
        if (this.compareWith) {
            return this.compareWith(a, b);
        }

        const aId = a?.[this.idField];
        const bId = b?.[this.idField];
        return aId != null && bId != null && String(aId) === String(bId);
    };

    trackByFn = (_: number, item: any): any => item?.[this.idField] ?? item;

    formatViewValue(v: any): string {
        if (v == null) {
            return '-';
        }

        if (this.returnType === 'id') {
            const found = (this.options ?? []).find(o => String(o?.[this.idField]) === String(v));
            return found ? this.labelFn(found) : String(v);
        }

        return this.labelFn(v);
    }

    isEditable(): boolean {
        return isEditableByMode({
            modeView: this.modeView,
            model: this.model,
            property: this.property ?? ''
        });
    }

    protected openlink(value: SelectValue) {
        this.activeModal.dismiss('cancel');
        const variableId = this.model?.['id'];
        setTimeout(() => {
            if (variableId) {
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', variableId, this.link, value.id, 'view'] } }],
                    { queryParamsHandling: 'merge' }
                );
            } else {
                this.router.navigate(
                    ['/', { outlets: { popup: [this.link, value.id, 'view'] } }],
                    { queryParamsHandling: 'merge' });
            }
        });
    }
}
